/**
 * MyUploadsPage.js
 * =================
 * Historique des uploads PDF mandats de l'agent. Permet de :
 * - Voir tous ses uploads passés
 * - Reprendre un upload non consommé (status="ready")
 * - Voir si un mandat a déjà été créé depuis cet upload
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  FileText, ArrowLeft, Clock, CheckCircle2, AlertCircle, Sparkles, Upload, ChevronRight,
} from 'lucide-react';

const STATUS_META = {
  extracting: { label: 'En cours…', color: 'bg-amber-500/15 text-amber-300 border-amber-500/30', icon: Clock },
  ready:      { label: 'Prêt à utiliser', color: 'bg-orange-500/15 text-orange-300 border-orange-500/30', icon: Sparkles },
  consumed:   { label: 'Mandat créé', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', icon: CheckCircle2 },
  error:      { label: 'Erreur', color: 'bg-red-500/15 text-red-300 border-red-500/30', icon: AlertCircle },
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins}min`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `il y a ${h}h`;
  const d = Math.floor(h / 24);
  return `il y a ${d}j`;
}

function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

export default function MyUploadsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const basePath = user?.role === 'admin' ? '/admin' : '/agent';
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/mandate-upload/my-uploads');
        setUploads(res.data?.uploads || []);
      } catch (e) {
        toast.error('Erreur chargement uploads');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const counts = {
    total: uploads.length,
    ready: uploads.filter(u => u.status === 'ready').length,
    consumed: uploads.filter(u => u.status === 'consumed').length,
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl" data-testid="my-uploads-page">
      <Button variant="ghost" onClick={() => navigate(`${basePath}/mandates`)} className="mb-4" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" />Mandats
      </Button>

      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
        className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FileText className="w-7 h-7 text-orange-400" />
            Mes uploads PDF
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {counts.total} upload(s) · {counts.consumed} mandat(s) créé(s) · {counts.ready} prêt(s) à utiliser
          </p>
        </div>
        <Button onClick={() => navigate(`${basePath}/mandates/upload`)} className="bg-orange-500 hover:bg-orange-600" data-testid="new-upload-btn">
          <Upload className="w-4 h-4 mr-2" />Nouvel upload
        </Button>
      </motion.div>

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : uploads.length === 0 ? (
        <div className="text-center py-16 rounded-xl border border-dashed" data-testid="empty-uploads">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">Aucun upload pour le moment.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(`${basePath}/mandates/upload`)}>
            <Upload className="w-4 h-4 mr-2" />Uploader un premier PDF
          </Button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="uploads-list">
          {uploads.map(u => {
            const meta = STATUS_META[u.status] || STATUS_META.ready;
            const Icon = meta.icon;
            const clickable = u.status === 'consumed' && u.mandate_id;
            return (
              <motion.div
                key={u.upload_id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => clickable && navigate(`${basePath}/mandates/v2/${u.mandate_id}`)}
                className={`flex items-center gap-3 p-4 rounded-xl bg-card border transition ${
                  clickable ? 'cursor-pointer hover:border-orange-500/40' : ''
                }`}
                data-testid={`upload-${u.upload_id}`}
              >
                <div className="w-10 h-10 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-orange-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{u.filename}</span>
                    <Badge variant="outline" className={`${meta.color} border text-xs flex items-center gap-1`}>
                      <Icon className="w-3 h-3" />{meta.label}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                    <span>{timeAgo(u.created_at)}</span>
                    <span>{formatSize(u.size)}</span>
                    {u.mandate_id && <span className="text-orange-300">Mandat associé</span>}
                  </div>
                </div>
                {clickable && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
